import { useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import type { InventoryItemNameRow } from '@/lib/api'
import { useStorefrontCategories } from '@/hooks/useStorefrontCategories'

type Props = {
  inventoryItems: InventoryItemNameRow[]
  inventoryLoading: boolean
}

const DEALS = '__deals__'

/** Chips on `/items` to jump between All, Clearance & deals and each category (scroll sideways on phones, wrap from `md`). */
export default function CategoryChips({ inventoryItems, inventoryLoading }: Props) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { visibleCategories } = useStorefrontCategories(inventoryItems, inventoryLoading)
  const activeRef = useRef<HTMLButtonElement>(null)

  const categoryId = searchParams.get('category') ?? ''
  const favoritesOnly = searchParams.get('favorites') === '1'
  const active = searchParams.get('deals') === '1' ? DEALS : favoritesOnly ? null : categoryId

  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest', inline: 'center' })
  }, [active, visibleCategories.length])

  const go = (value: string) => {
    const next = new URLSearchParams(searchParams)
    next.delete('category')
    next.delete('favorites')
    next.delete('deals')
    if (value === DEALS) next.set('deals', '1')
    else if (value) next.set('category', value)
    navigate({ pathname: '/items', search: next.toString() })
  }

  const chips = [
    { value: '', label: 'All' },
    { value: DEALS, label: 'Clearance & deals' },
    ...visibleCategories.map((c) => ({ value: c.category_id, label: c.name })),
  ]

  return (
    <nav
      aria-label="Categories"
      className="-mx-4 flex gap-1.5 overflow-x-auto px-4 [scrollbar-width:none] sm:gap-2 md:mx-0 md:flex-wrap md:overflow-visible md:px-0 [&::-webkit-scrollbar]:hidden"
    >
      {chips.map((c) => {
        const isActive = c.value === active
        return (
          <button
            key={c.value || 'all'}
            ref={isActive ? activeRef : undefined}
            type="button"
            onClick={() => go(c.value)}
            aria-current={isActive ? 'page' : undefined}
            className={`shrink-0 whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium transition-colors sm:py-1.5 sm:text-sm ${
              isActive
                ? 'border-primary-500 bg-primary-500 text-white'
                : 'border-stone-200 bg-white text-stone-700 hover:border-primary-300 hover:bg-primary-50'
            }`}
          >
            {c.label}
          </button>
        )
      })}
    </nav>
  )
}
